import { queryOptions } from "@tanstack/react-query";

/**
 * The staff door's session (`/api/staff/session`): who is signed in as staff, if anyone, and what the page shows next
 * (the sign-in, "add a passkey", or the staff pages). Staff are their own accounts with their own cookie: nothing here
 * touches the product's session. The CSRF token lives in memory only and goes out on every staff write.
 */
export type StaffSessionState =
  | {
      signedIn: false;
      /** The staff door answers on adea.app only: on a company's address there are no staff pages. */
      notHere?: true;
      /** "Continue with Google" is set up on this server. */
      google?: boolean;
    }
  | {
      signedIn: true;
      google?: boolean;
      staff: { name: string; email: string; role: "sysadmin" | "operator" };
      /** False for a session from a mailed link: a passkey comes first. */
      passkeyDone: boolean;
      hasPasskey: boolean;
      /** A mailed link's session in its first 15 minutes: it may add a passkey on this device. */
      mayAddPasskey: boolean;
      endsAt: string;
      csrf: string;
    };

let staffCsrf: string | null = null;

export function getStaffCsrf(): string | null {
  return staffCsrf;
}

export const staffSessionQuery = queryOptions({
  queryKey: ["staff-session"] as const,
  queryFn: async ({ signal }): Promise<StaffSessionState> => {
    const response = await fetch("/api/staff/session", { signal, headers: { accept: "application/json" } });
    if (response.status === 404) return { signedIn: false, notHere: true };
    if (!response.ok) throw new Error(`staff session: ${response.status}`);
    const state = (await response.json()) as StaffSessionState;
    staffCsrf = state.signedIn ? state.csrf : null;
    return state;
  },
  staleTime: 60_000,
});

/** A write to the staff door outside the actions: JSON in, `{ ok, problem? }` back. */
export async function staffPost<T = Record<string, unknown>>(
  path: string,
  body: unknown = {},
): Promise<{ ok: boolean; status: number; problem?: string } & Partial<T>> {
  const headers: Record<string, string> = { accept: "application/json", "content-type": "application/json" };
  if (staffCsrf) headers["x-adea-staff-csrf"] = staffCsrf;
  let response: Response;
  try {
    response = await fetch(`/api/staff${path}`, { method: "POST", headers, body: JSON.stringify(body) });
  } catch {
    return { ok: false, status: 0, problem: "offline" } as {
      ok: boolean;
      status: number;
      problem?: string;
    } & Partial<T>;
  }
  const data = (await response.json().catch(() => ({}))) as Partial<T> & { problem?: string };
  return { ...data, ok: response.ok, status: response.status };
}
